import { useState, type CSSProperties } from 'react';
import { Check, Minus } from 'lucide-react';
import { Button } from '@/design-system/primitives/Button';
import { Segmented } from '@/design-system/primitives/Segmented';
import {
  ENTERPRISE_HIGHLIGHTS,
  ENTERPRISE_TIER,
  MARKETING_FEATURE_ROWS,
  MARKETING_TIERS,
  PRICED_TIERS,
  type MarketingRowValue,
  type MarketingTier,
  type MarketingTierId,
} from '@/modules/billing/marketing-tiers';

interface PricingTableProps {
  onSelectPlan: (tier: MarketingTierId, cadence: 'monthly' | 'annual') => void;
  /** Where "Contact sales" goes. Enterprise is negotiated and has no checkout. */
  enterpriseHref?: string;
}

const DEFAULT_ENTERPRISE_HREF = 'mailto:sales@integritty.app?subject=Integritty%20Enterprise';

const glassCard: CSSProperties = {
  background: 'var(--ds-glass-card-fill)',
  border: '1px solid var(--ds-glass-card-border)',
  backdropFilter: 'var(--ds-glass-blur)',
  WebkitBackdropFilter: 'var(--ds-glass-blur)',
  borderRadius: 'var(--ds-radius-lg)',
  boxShadow: 'var(--ds-shadow-sm)',
};

/**
 * One row of the comparison grid, for one rung. An absence renders in full
 * rather than being dropped: a visitor on Free is meant to see what the rung
 * above would give them. A rung that differs by degree replaces the whole line
 * rather than appending a qualifier, because the cards are too narrow to hold
 * both.
 */
function FeatureLine({ label, value }: { label: string; value: MarketingRowValue }) {
  const absent = value === false;
  const text = typeof value === 'string' ? value : label;

  return (
    <div className="flex items-start gap-2.5 text-sm">
      {absent ? (
        <Minus
          className="w-4 h-4 shrink-0 mt-0.5"
          strokeWidth={2}
          style={{ color: 'hsl(var(--ds-text-meta) / 0.55)' }}
        />
      ) : (
        <Check
          className="w-4 h-4 shrink-0 mt-0.5"
          strokeWidth={2}
          style={{ color: 'hsl(var(--ds-brand-purple-fg))' }}
        />
      )}
      <span style={{ color: `hsl(var(--ds-text-${absent ? 'meta' : 'strong'}))` }}>{text}</span>
    </div>
  );
}

function TierCard({
  tier,
  cadence,
  onSelect,
}: {
  tier: MarketingTier;
  cadence: 'monthly' | 'annual';
  onSelect: () => void;
}) {
  const price = tier.price;
  const shown = price ? (cadence === 'annual' ? price.annualMonthly : price.monthly) : null;

  return (
    <div
      className="p-6 flex flex-col relative"
      style={
        tier.highlighted
          ? { ...glassCard, border: '1px solid hsl(var(--ds-brand-purple) / 0.35)', boxShadow: 'var(--ds-shadow-md)' }
          : glassCard
      }
    >
      {tier.badge && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span
            className="text-xs font-semibold px-3 py-1 whitespace-nowrap inline-block"
            style={{
              fontFamily: 'var(--ds-font-display)',
              background: 'var(--ds-brand-gradient)',
              color: '#fff',
              borderRadius: 'var(--ds-radius-pill)',
              boxShadow: '0 6px 14px -8px hsl(285 75% 50% / 0.7)',
            }}
          >
            {tier.badge}
          </span>
        </div>
      )}

      <div>
        <p
          className="text-lg"
          style={{ fontFamily: 'var(--ds-font-display)', fontWeight: 700, color: 'hsl(var(--ds-text-heading))' }}
        >
          {tier.name}
        </p>
        <p className="text-sm mt-1 min-h-[2.5rem]" style={{ color: 'hsl(var(--ds-text-meta))' }}>
          {tier.tagline}
        </p>
      </div>

      <div className="mt-5">
        <div className="flex items-baseline gap-1">
          <span
            className="text-4xl tabular-nums"
            style={{
              fontFamily: 'var(--ds-font-display)',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: 'hsl(var(--ds-text-heading))',
            }}
          >
            ${shown}
          </span>
          {tier.id !== 'free' && (
            <span className="text-sm" style={{ color: 'hsl(var(--ds-text-meta))' }}>
              /month
            </span>
          )}
        </div>
        <p className="text-xs mt-1 tabular-nums" style={{ color: 'hsl(var(--ds-text-meta))' }}>
          {tier.id === 'free'
            ? 'Free forever'
            : cadence === 'annual'
              ? `Billed $${price?.annualTotal.toLocaleString()}/year`
              : 'Billed monthly'}
        </p>
      </div>

      <Button
        variant={tier.highlighted ? 'primary' : 'secondary'}
        style={{ width: '100%', marginTop: 20 }}
        onClick={onSelect}
      >
        {tier.cta}
      </Button>
      {tier.ctaNote && (
        <p className="text-xs mt-2 text-center" style={{ color: 'hsl(var(--ds-text-meta))' }}>
          {tier.ctaNote}
        </p>
      )}

      <div className="mt-6 pt-5 space-y-2.5" style={{ borderTop: '1px solid var(--ds-divider)' }}>
        {MARKETING_FEATURE_ROWS.map((row) => (
          <FeatureLine key={row.label} label={row.label} value={row.values[tier.id]} />
        ))}
      </div>
    </div>
  );
}

function EnterpriseBand({ href }: { href: string }) {
  return (
    <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6" style={glassCard}>
      <div className="md:w-1/3">
        <p
          className="text-lg"
          style={{ fontFamily: 'var(--ds-font-display)', fontWeight: 700, color: 'hsl(var(--ds-text-heading))' }}
        >
          {ENTERPRISE_TIER.name}
        </p>
        <p className="text-sm mt-1" style={{ color: 'hsl(var(--ds-text-meta))' }}>
          {ENTERPRISE_TIER.tagline}
        </p>
      </div>

      <div className="md:flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
        <p className="text-xs font-semibold uppercase tracking-wide sm:col-span-2" style={{ color: 'hsl(var(--ds-text-meta))' }}>
          Everything in Scale, plus
        </p>
        {ENTERPRISE_HIGHLIGHTS.map((label) => (
          <FeatureLine key={label} label={label} value={true} />
        ))}
      </div>

      <div className="md:w-auto">
        <Button variant="secondary" onClick={() => { window.location.href = href; }}>
          {ENTERPRISE_TIER.cta}
        </Button>
      </div>
    </div>
  );
}

export function PricingTable({ onSelectPlan, enterpriseHref }: PricingTableProps) {
  const [cadence, setCadence] = useState<'monthly' | 'annual'>('monthly');

  const savingsPct = MARKETING_TIERS.find((t) => t.id === 'grow')?.annualSavingsPct ?? null;

  return (
    <div className="space-y-8">
      <div className="flex flex-col items-center gap-2">
        <Segmented
          options={[
            { label: 'Monthly', value: 'monthly' },
            { label: 'Annual', value: 'annual' },
          ]}
          value={cadence}
          onChange={(next) => setCadence(next as 'monthly' | 'annual')}
        />
        {savingsPct != null && savingsPct > 0 && (
          <p className="text-xs font-semibold tabular-nums" style={{ color: 'hsl(var(--ds-brand-purple-fg))' }}>
            Save {savingsPct}% with annual billing
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        {PRICED_TIERS.map((tier) => (
          <TierCard
            key={tier.id}
            tier={tier}
            cadence={cadence}
            onSelect={() => onSelectPlan(tier.id, cadence)}
          />
        ))}
      </div>

      <EnterpriseBand href={enterpriseHref ?? DEFAULT_ENTERPRISE_HREF} />
    </div>
  );
}
